import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { performanceMonitor } from '../utils/performanceMonitor';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second base delay

// Abort controller map for cleanup
const abortControllers = new Map<string, AbortController>();

// Extender tipos para incluir metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
    metadata?: { startTime: number };
    retries?: number;
}

// Cliente HTTP unificado para toda la aplicación
const httpClient = axios.create({
    baseURL: import.meta.env.VITE_BACKEND_URL || '',
    timeout: 30000, // 30 segundos timeout para consultas complejas
    headers: {
        'Content-Type': 'application/json'
    }
});

// Performance: Interceptor para medir tiempos de respuesta
httpClient.interceptors.request.use(
    (config: ExtendedAxiosRequestConfig) => {
        // Agregar timestamp para métricas
        config.metadata = { startTime: Date.now() };

        // Agregar token automáticamente
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Agregar tenant automáticamente (x-tenant-id es el header que espera el backend)
        const tenantId = localStorage.getItem('tenantId');
        if (tenantId) {
            config.headers['x-tenant-id'] = tenantId;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor de respuesta con métricas y manejo de errores
httpClient.interceptors.response.use(
    (response: AxiosResponse & { config: ExtendedAxiosRequestConfig }) => {
        // Métricas de performance
        const duration = Date.now() - (response.config.metadata?.startTime || Date.now());

        // Log performance metrics
        performanceMonitor.logMetric(response.config.url || '', duration, 'success');

        if (duration > 2000) {
            console.warn(`⚠️ Slow API call: ${response.config.url} (${duration}ms)`);
        }

        // Validar que la respuesta sea JSON válido
        if (response.data && typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
            console.error('❌ Recibiendo HTML en lugar de JSON:', response.config.url);
            throw new Error('El servidor está devolviendo HTML en lugar de datos JSON. Verifica que el backend esté ejecutándose correctamente.');
        }

        return response;
    },
    (error) => {
        // Log error metrics
        const duration = Date.now() - (error.config?.metadata?.startTime || Date.now());
        performanceMonitor.logMetric(error.config?.url || '', duration, 'error');

        // Enhanced debugging for 400 errors
        if (error.response?.status === 400) {
            console.error('❌ 400 Bad Request Error Details:', {
                url: error.config?.url,
                method: error.config?.method,
                headers: {
                    authorization: error.config?.headers?.Authorization ? 'Present' : 'Missing',
                    'content-type': error.config?.headers?.['Content-Type'],
                },
                data: error.response?.data,
                message: error.message,
                requestBody: error.config?.data
            });
        }

        // Manejo unificado de errores
        if (error.response?.status === 401) {
            // Solo limpiar token en errores de autenticación real
            if (error.response.data?.message?.includes('token') ||
                error.response.data?.error?.includes('token') ||
                error.config.url !== '/api/auth/verify') {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        }

        // Verificar si estamos recibiendo HTML en lugar de JSON en errores
        if (error.response?.data && typeof error.response.data === 'string' &&
            error.response.data.includes('<!doctype html>')) {
            console.error('❌ Error: Recibiendo HTML en lugar de JSON');
            return Promise.reject(new Error('El servidor no está respondiendo correctamente. Verifica que el backend esté ejecutándose.'));
        }

        return Promise.reject(error);
    }
);

// Retry logic interceptor
httpClient.interceptors.response.use(
    undefined,
    async (error: AxiosError) => {
        const config = error.config as ExtendedAxiosRequestConfig & { retries?: number };

        if (!config || config.retries === undefined) {
            config.retries = 0;
        }

        // Only retry on network errors or 5xx errors
        const shouldRetry =
            !error.response && // Network error
            config.retries < MAX_RETRIES &&
            !error.code?.includes('ECONNABORTED'); // Not timeout

        if (shouldRetry) {
            config.retries += 1;
            const delay = RETRY_DELAY * Math.pow(2, config.retries - 1); // Exponential backoff

            console.log(`🔄 Retrying request (${config.retries}/${MAX_RETRIES}) after ${delay}ms: ${config.url}`);

            await new Promise(resolve => setTimeout(resolve, delay));
            return httpClient(config);
        }

        return Promise.reject(error);
    }
);

// Performance optimization: Add request deduplication for repeated calls
const pendingRequests = new Map();

// Intercept requests to deduplicate identical calls
const originalGet = httpClient.get;
httpClient.get = function (url, config) {
    const key = `GET:${url}:${JSON.stringify(config)}`;

    if (pendingRequests.has(key)) {
        return pendingRequests.get(key);
    }

    const promise = originalGet.call(this, url, config).finally(() => {
        pendingRequests.delete(key);
    });

    pendingRequests.set(key, promise);
    return promise;
};

// AbortController support
export const createCancellableRequest = (requestId: string) => {
    const controller = new AbortController();
    abortControllers.set(requestId, controller);

    return {
        signal: controller.signal,
        cancel: () => {
            controller.abort();
            abortControllers.delete(requestId);
        }
    };
};

export const cancelRequest = (requestId: string) => {
    const controller = abortControllers.get(requestId);
    if (controller) {
        controller.abort();
        abortControllers.delete(requestId);
    }
};

export const cancelAllRequests = () => {
    abortControllers.forEach(controller => controller.abort());
    abortControllers.clear();
};

export default httpClient;
